import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  LogOut, 
  MapPin,
  Settings,
  UserCog,
  Building2
} from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const navItems = {
    collaborateur: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Mon espace" },
      { to: "/mon-compte", icon: UserCog, label: "Mon compte" },
    ],
    manager: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Mon espace" },
      { to: "/manager", icon: Users, label: "Mon équipe" },
      { to: "/mon-compte", icon: UserCog, label: "Mon compte" },
    ],
    directeur: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Mon espace" },
      { to: "/manager", icon: Users, label: "Mon équipe" },
      { to: "/mon-compte", icon: UserCog, label: "Mon compte" },
    ],
    rh: [
      { to: "/manager", icon: Users, label: "Gestion équipes" },
      { to: "/rh", icon: FileText, label: "Tableau de bord" },
      { to: "/rh/admin", icon: Building2, label: "Collaborateurs" },
      { to: "/rh/settings", icon: Settings, label: "Paramètres RH" },
      { to: "/mon-compte", icon: UserCog, label: "Mon compte" },
    ]
  };

  const items = navItems[user?.role] || navItems.collaborateur;

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-md flex-shrink-0">
            <img 
              src="/logo-pitstop.png" 
              alt="Pitstop" 
              className="h-7 w-7 object-contain"
            />
          </div>
          <div className="min-w-0">
            <h1 
              className="text-xl font-black tracking-tight text-white"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              Pitstop
            </h1>
            <p className="text-[10px] text-gray-400 leading-tight truncate">by Verbaere Performance Suite</p>
          </div>
        </div>
      </div>

      {/* User info */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 bg-[#00A3E0] text-white flex-shrink-0">
            <AvatarFallback className="bg-[#00A3E0] text-white font-semibold">
              {getInitials(user?.first_name, user?.last_name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                isActive
                  ? "bg-[#00A3E0] text-white font-medium"
                  : "text-gray-300 hover:bg-white/10 hover:text-white active:bg-white/20"
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Concession badge */}
      {user?.concession && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg">
            <MapPin className="h-4 w-4 text-[#00A3E0] flex-shrink-0" />
            <span className="text-xs text-gray-400 truncate">{user.concession}</span>
          </div>
        </div>
      )}

      {/* Logout */}
      <div className="p-4 border-t border-white/10">
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/20"
          data-testid="logout-button"
        >
          <LogOut className="h-5 w-5 mr-3 flex-shrink-0" strokeWidth={1.5} />
          Déconnexion
        </Button>
      </div>
    </>
  );

  return (
    <aside className="w-64 bg-[#1a2634] flex flex-col min-h-screen overflow-hidden">
      <SidebarContent />
    </aside>
  );
};
